import React, { useState, useEffect, useMemo } from 'react';
import ExamSetup from './ExamSetup';
import MarksEntry from './MarksEntry';
import ReportCards from './ReportCards';
import GradingSettings from './GradingSettings';
import ExamTypeSettings from './ExamTypeSettings';
import ReportCardTemplateDesigner from '../ReportCardTemplateDesigner.jsx';
import HallTicketGenerator from './HallTicketGenerator';
import { reportCardService, studentService, classService } from '../../../services/index.js';
import { Calendar, ClipboardCheck, FileText, Settings, Award, Palette, RefreshCw, ListChecks, Ticket } from 'lucide-react';

const STANDARD_SCHOOL_CLASSES = [
  { className: 'LKG' }, { className: 'UKG' },
  { className: 'Class 1' }, { className: 'Class 2' }, { className: 'Class 3' },
  { className: 'Class 4' }, { className: 'Class 5' }, { className: 'Class 6' },
  { className: 'Class 7' }, { className: 'Class 8' }, { className: 'Class 9' },
  { className: 'Class 10' }
];

const Examinations = ({
  orgData,
  students: propStudents = [],
  classes: propClasses = [],
  teachers = [],
  showSuccessNotification,
  showErrorNotification
}) => {
  // Main Tab Navigation: 'exams' | 'marks' | 'cards' | 'halltickets' | 'settings'
  const [activeTab, setActiveTab] = useState('exams');
  const [hallTicketPreselect, setHallTicketPreselect] = useState(null);
  const [examSetupPreselect, setExamSetupPreselect] = useState(null);
  // Settings sub-tab: 'grading' | 'assignments' | 'designer'
  const [settingsSubTab, setSettingsSubTab] = useState('grading');

  // Resolve initial academic year dynamically from tenant orgData, localStorage or current school calendar year
  const resolveActiveAcademicYear = () => {
    if (orgData?.academicYear) return orgData.academicYear;
    if (orgData?.activeAcademicYear) return orgData.activeAcademicYear;
    const stored = localStorage.getItem('academicYear') || localStorage.getItem('activeAcademicYear');
    if (stored) return stored;
    const now = new Date();
    const curYear = now.getFullYear();
    const startYear = now.getMonth() >= 4 ? curYear : curYear - 1;
    return `${startYear}-${(startYear + 1).toString().slice(-2)}`;
  };

  const [academicYear, setAcademicYear] = useState(resolveActiveAcademicYear);
  const [tests, setTests] = useState([]);
  const [reportCards, setReportCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allStudents, setAllStudents] = useState(propStudents || []);
  const [allClasses, setAllClasses] = useState(propClasses && propClasses.length > 0 ? propClasses : STANDARD_SCHOOL_CLASSES);
  const [examTypeConfigs, setExamTypeConfigs] = useState([]);

  // Sync if orgData updates with tenant academic year
  useEffect(() => {
    if (orgData?.academicYear || orgData?.activeAcademicYear) {
      const tenantYear = orgData.academicYear || orgData.activeAcademicYear;
      if (tenantYear && tenantYear !== academicYear) {
        setAcademicYear(tenantYear);
      }
    }
  }, [orgData?.academicYear, orgData?.activeAcademicYear]);

  // Load students & classes from DB if not provided by parent
  useEffect(() => {
    let orgId = orgData?.orgId || localStorage.getItem('orgId');
    if (!orgId) {
      try {
        const uStr = localStorage.getItem('user');
        if (uStr) {
          const u = JSON.parse(uStr);
          orgId = u.orgId || u.organizationId;
        }
      } catch (e) {}
    }

    if (!propStudents || propStudents.length === 0) {
      studentService.getStudents(orgId).then(res => {
        if (Array.isArray(res)) setAllStudents(res);
        else if (res && Array.isArray(res.students)) setAllStudents(res.students);
        else if (res && Array.isArray(res.data)) setAllStudents(res.data);
      }).catch(err => console.error('Failed to load students in Examinations:', err));
    } else {
      setAllStudents(propStudents);
    }

    if (!propClasses || propClasses.length === 0) {
      classService.getClassesByOrg(orgId).then(res => {
        if (Array.isArray(res) && res.length > 0) setAllClasses(res);
      }).catch(err => console.error('Failed to load classes in Examinations:', err));
    } else {
      setAllClasses(propClasses);
    }
  }, [propStudents, propClasses, orgData?.orgId]);

  // Dynamically compute list of available academic years from DB records + tenant + rolling school window
  const availableAcademicYears = useMemo(() => {
    const yearsSet = new Set();

    // 1. From orgData
    if (orgData?.academicYear) yearsSet.add(orgData.academicYear);
    if (orgData?.activeAcademicYear) yearsSet.add(orgData.activeAcademicYear);
    if (Array.isArray(orgData?.academicYears)) {
      orgData.academicYears.forEach(y => y && yearsSet.add(y));
    }

    // 2. From localStorage
    const stored = localStorage.getItem('academicYear') || localStorage.getItem('activeAcademicYear');
    if (stored) yearsSet.add(stored);

    // 3. Current active selection
    if (academicYear) yearsSet.add(academicYear);

    // 4. From Classes
    allClasses.forEach(c => {
      if (c.academicYear) yearsSet.add(c.academicYear);
    });

    // 5. From Students
    allStudents.forEach(s => {
      if (s.academicYear) yearsSet.add(s.academicYear);
      if (s.admissionYear) yearsSet.add(s.admissionYear);
    });

    // 6. From Tests
    tests.forEach(t => {
      if (t.academicYear) yearsSet.add(t.academicYear);
      if (t.description && /^\d{4}-\d{2,4}$/.test(t.description.trim())) {
        yearsSet.add(t.description.trim());
      }
    });

    // 7. Rolling 5-year school window
    const currentYear = new Date().getFullYear();
    for (let i = -2; i <= 2; i++) {
      const start = currentYear + i;
      const shortEnd = (start + 1).toString().slice(-2);
      yearsSet.add(`${start}-${shortEnd}`);
    }

    return Array.from(yearsSet).filter(Boolean).sort().reverse();
  }, [orgData, academicYear, allClasses, allStudents, tests]);

  // Load tests and report cards globally for the selected academic year
  const loadGlobalData = async () => {
    setLoading(true);
    try {
      const response = await reportCardService.getTests();
      let testList = [];
      if (response && response.success && Array.isArray(response.tests)) {
        testList = response.tests;
      } else if (Array.isArray(response)) {
        testList = response;
      } else if (response && Array.isArray(response.data)) {
        testList = response.data;
      }
      setTests(testList);

      const rcRes = await reportCardService.listByOrg({ academicYear });
      setReportCards(Array.isArray(rcRes) ? rcRes : []);

      // Load exam type configs for this academic year
      try {
        const etRes = await reportCardService.getExamTypeConfig(academicYear);
        if (etRes && etRes.success && Array.isArray(etRes.examTypes)) {
          setExamTypeConfigs(etRes.examTypes);
        } else {
          setExamTypeConfigs([]);
        }
      } catch (etErr) {
        setExamTypeConfigs([]);
      }
    } catch (err) {
      console.error('Failed to load exam core data in Examinations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGlobalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear, activeTab]);

  // Only official school exams for metrics
  const schoolTests = useMemo(() => {
    return tests.filter(t => {
      if (t.createdByRole === 'TEACHER' && t.teacherId && t.teacherId !== 'ADMIN' && t.testType !== 'EXAM' && t.testType !== 'SCHOOL_EXAM') {
        return false;
      }
      return true;
    });
  }, [tests]);

  const teacherAssignedTests = useMemo(() => {
    return tests.filter(t => {
      return (t.createdByRole === 'TEACHER' || (t.teacherId && t.teacherId !== 'ADMIN')) && t.testType !== 'EXAM' && t.testType !== 'SCHOOL_EXAM';
    });
  }, [tests]);

  // Compact metrics
  const metrics = useMemo(() => {
    const distinctExams = new Set(schoolTests.map(t => `${t.title}-${t.className}`));
    const totalExams = distinctExams.size;
    const teacherTestsCount = teacherAssignedTests.length;
    const marksPending = schoolTests.filter(t => t.status === 'SCHEDULED').length;
    const reportsReady = reportCards.filter(rc => rc.status === 'DRAFT').length;
    const published = reportCards.filter(rc => rc.status === 'PUBLISHED' || rc.status === 'LOCKED').length;

    return {
      totalExams,
      teacherTestsCount,
      marksPending,
      reportsReady,
      published
    };
  }, [schoolTests, teacherAssignedTests, reportCards]);

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '20px 24px', width: '100%', boxSizing: 'border-box' }}>
      
      {/* 1. Header Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>
            School Exams
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
            Schedule and manage official school examinations, enter marks, and generate report cards.
          </p>
        </div>

        {/* Dynamic Academic Year Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569' }}>Session:</span>
          <select
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1.5px solid #cbd5e1',
              fontSize: '13px',
              fontWeight: '700',
              color: '#0f172a',
              background: '#ffffff',
              cursor: 'pointer'
            }}
          >
            {availableAcademicYears.map(yr => (
              <option key={yr} value={yr}>{yr} Session</option>
            ))}
          </select>
          <button
            type="button"
            onClick={loadGlobalData}
            title="Refresh examination data"
            style={{
              padding: '8px 10px',
              borderRadius: '8px',
              border: '1.5px solid #cbd5e1',
              background: '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#334155'
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* 2. Compact Metric Summary Counters (Section 17) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{ background: '#ffffff', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Exams Scheduled</div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>{metrics.totalExams}</div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Teacher Tests</div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: '#0891b2', marginTop: '2px' }}>{metrics.teacherTestsCount}</div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Marks Pending</div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: '#ea580c', marginTop: '2px' }}>{metrics.marksPending}</div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Reports Ready</div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: '#2563eb', marginTop: '2px' }}>{metrics.reportsReady}</div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Published Cards</div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: '#16a34a', marginTop: '2px' }}>{metrics.published}</div>
        </div>
      </div>

      {/* 3. Primary 4-Tab Navigation Bar (Section 4) */}
      <div style={{
        display: 'flex',
        gap: '8px',
        background: '#e2e8f0',
        padding: '5px',
        borderRadius: '10px',
        width: 'fit-content',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        {/* Tab 1: School Exams */}
        <button
          type="button"
          onClick={() => setActiveTab('exams')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            borderRadius: '8px',
            fontSize: '13.5px',
            fontWeight: '700',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'exams' ? '#ffffff' : 'transparent',
            color: activeTab === 'exams' ? '#1e3a8a' : '#475569',
            boxShadow: activeTab === 'exams' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <Calendar size={15} /> 1. School Exams
        </button>

        {/* Tab 2: Marks */}
        <button
          type="button"
          onClick={() => setActiveTab('marks')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            borderRadius: '8px',
            fontSize: '13.5px',
            fontWeight: '700',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'marks' ? '#ffffff' : 'transparent',
            color: activeTab === 'marks' ? '#1e3a8a' : '#475569',
            boxShadow: activeTab === 'marks' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <ClipboardCheck size={15} /> 2. Marks
        </button>

        {/* Tab 3: Report Cards */}
        <button
          type="button"
          onClick={() => setActiveTab('cards')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            borderRadius: '8px',
            fontSize: '13.5px',
            fontWeight: '700',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'cards' ? '#ffffff' : 'transparent',
            color: activeTab === 'cards' ? '#1e3a8a' : '#475569',
            boxShadow: activeTab === 'cards' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <FileText size={15} /> 3. Report Cards
        </button>

        {/* Tab 4: Hall Tickets */}
        <button
          type="button"
          onClick={() => setActiveTab('halltickets')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            borderRadius: '8px',
            fontSize: '13.5px',
            fontWeight: '700',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'halltickets' ? '#ffffff' : 'transparent',
            color: activeTab === 'halltickets' ? '#1e3a8a' : '#475569',
            boxShadow: activeTab === 'halltickets' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <Ticket size={15} /> 4. 🎫 Hall Tickets
        </button>

        {/* Tab 5: Settings */}
        <button
          type="button"
          onClick={() => setActiveTab('settings')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            borderRadius: '8px',
            fontSize: '13.5px',
            fontWeight: '700',
            border: 'none',
            cursor: 'pointer',
            background: activeTab === 'settings' ? '#ffffff' : 'transparent',
            color: activeTab === 'settings' ? '#1e3a8a' : '#475569',
            boxShadow: activeTab === 'settings' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <Settings size={15} /> 5. Settings
        </button>
      </div>

      {/* 4. Tab Workspaces */}
      <div>
        {/* TAB 1: EXAMS & TESTS */}
        {activeTab === 'exams' && (
          <ExamSetup
            orgData={orgData}
            classes={allClasses}
            teachers={teachers}
            tests={tests}
            loadTests={loadGlobalData}
            activeAcademicYear={academicYear}
            availableAcademicYears={availableAcademicYears}
            examTypeConfigs={examTypeConfigs}
            onOpenHallTickets={(params) => {
              setHallTicketPreselect(params);
              setActiveTab('halltickets');
            }}
            editPreselect={examSetupPreselect}
            onClearPreselect={() => setExamSetupPreselect(null)}
            showSuccessNotification={showSuccessNotification}
            showErrorNotification={showErrorNotification}
          />
        )}

        {/* TAB 2: MARKS ENTRY */}
        {activeTab === 'marks' && (
          <MarksEntry
            orgData={orgData}
            students={allStudents}
            classes={allClasses}
            tests={tests}
            activeAcademicYear={academicYear}
            availableAcademicYears={availableAcademicYears}
            examTypeConfigs={examTypeConfigs}
            refreshTests={loadGlobalData}
            showSuccessNotification={showSuccessNotification}
            showErrorNotification={showErrorNotification}
          />
        )}

        {/* TAB 3: REPORT CARDS */}
        {activeTab === 'cards' && (
          <ReportCards
            orgData={orgData}
            students={allStudents}
            classes={allClasses}
            tests={tests}
            activeAcademicYear={academicYear}
            availableAcademicYears={availableAcademicYears}
            examTypeConfigs={examTypeConfigs}
            onOpenDesigner={() => {
              setActiveTab('settings');
              setSettingsSubTab('designer');
            }}
            showSuccessNotification={showSuccessNotification}
            showErrorNotification={showErrorNotification}
          />
        )}

        {/* TAB 4: 🎫 HALL TICKETS */}
        {activeTab === 'halltickets' && (
          <HallTicketGenerator
            orgData={orgData}
            students={allStudents}
            classes={allClasses}
            tests={tests}
            activeAcademicYear={academicYear}
            availableAcademicYears={availableAcademicYears}
            examTypeConfigs={examTypeConfigs}
            preselect={hallTicketPreselect}
            onOpenExamSetup={(params) => {
              setExamSetupPreselect(params);
              setActiveTab('exams');
            }}
            showSuccessNotification={showSuccessNotification}
            showErrorNotification={showErrorNotification}
          />
        )}

        {/* TAB 5: SETTINGS (Grading, Class Template Mapping, Advanced Designer) */}
        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {/* Settings Secondary Navigation */}
            <div style={{
              display: 'flex',
              gap: '6px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '10px',
              marginBottom: '10px'
            }}>
              <button
                type="button"
                onClick={() => setSettingsSubTab('grading')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  background: settingsSubTab === 'grading' ? '#1e3a8a' : '#f1f5f9',
                  color: settingsSubTab === 'grading' ? '#ffffff' : '#475569'
                }}
              >
                <Award size={14} /> Grading Scales
              </button>

              <button
                type="button"
                onClick={() => setSettingsSubTab('examTypes')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  background: settingsSubTab === 'examTypes' ? '#1e3a8a' : '#f1f5f9',
                  color: settingsSubTab === 'examTypes' ? '#ffffff' : '#475569'
                }}
              >
                <ListChecks size={14} /> Exam Types
              </button>

              <button
                type="button"
                onClick={() => setSettingsSubTab('designer')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  background: settingsSubTab === 'designer' ? '#1e3a8a' : '#f1f5f9',
                  color: settingsSubTab === 'designer' ? '#ffffff' : '#475569'
                }}
              >
                <Palette size={14} /> Advanced Template Designer
              </button>
            </div>

            {/* Sub-tab A: Grading Settings */}
            {settingsSubTab === 'grading' && (
              <GradingSettings
                orgData={orgData}
                showSuccessNotification={showSuccessNotification}
                showErrorNotification={showErrorNotification}
              />
            )}

            {/* Sub-tab B: Exam Types */}
            {settingsSubTab === 'examTypes' && (
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }}>
                <ExamTypeSettings
                  activeAcademicYear={academicYear}
                  onSaved={loadGlobalData}
                  showSuccessNotification={showSuccessNotification}
                  showErrorNotification={showErrorNotification}
                />
              </div>
            )}

            {/* Sub-tab B: Advanced Template Designer */}
            {settingsSubTab === 'designer' && (
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '18px', border: '1px solid #e2e8f0' }}>
                <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                    🎨 Advanced Report Card Template Designer
                  </h4>
                  <p style={{ margin: '3px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
                    For advanced administrators: customize report card layouts, headers, grading legends, and column mappings.
                  </p>
                </div>
                <ReportCardTemplateDesigner
                  orgId={orgData?.orgId || localStorage.getItem('orgId') || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user'))?.orgId : '')}
                  academicYear={academicYear}
                  examTypeConfigs={examTypeConfigs}
                  branding={orgData}
                  orgName={orgData?.orgName}
                  orgAddress={orgData?.address}
                />
              </div>
            )}

          </div>
        )}
      </div>

    </div>
  );
};

export default Examinations;
